import { KaizenTheme } from '@kaizen-ui/styles'
import { css } from 'styled-components'

const styledSlider = css`
  position: relative;
  margin-top: ${({ theme }) => theme.spacing.six};
  box-sizing: border-box;

  .rs-slider-bar {
    height: 4px;
    border-radius: 2px;
    background-color: ${({ theme }) => theme.colors.slider.rail.background};
    cursor: pointer;
    box-sizing: border-box;
    box-shadow: ${({ theme }) => theme.colors.slider.rail.shadow};

    &:hover {
      background-color: ${({ theme }) => theme.colors.slider.rail.background};
    }
  }

  .rs-slider-progress-bar {
    position: absolute;
    height: 4px;
    border-radius: 2px 0 0 2px;
    background-color: ${({ theme }) => theme.colors.slider.track.background};
    box-sizing: border-box;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.15);
  }

  .rs-slider-handle {
    position: absolute;
    top: -0.6rem;
    transform: translateX(-0.25rem);

    &::before {
      content: '';
      position: absolute;
      width: 1.375rem;
      height: 1.375rem;
      border-radius: 50%;
      border: 3px solid ${({ theme }) => theme.colors.slider.handle.border};
      margin-left: -0.5rem;
      cursor: grab;
      transform: scale(1, 1);
      -webkit-transition: background-color 0.3s ease-in-out,
        -webkit-box-shadow 0.3s ease-in-out, -webkit-transform 0.3s ease-in-out;
      transition: background-color 0.3s ease-in-out,
        -webkit-box-shadow 0.3s ease-in-out, -webkit-transform 0.3s ease-in-out;
      transition: box-shadow 0.3s ease-in-out, background-color 0.3s ease-in-out,
        transform 0.3s ease-in-out;
      transition: box-shadow 0.3s ease-in-out, background-color 0.3s ease-in-out,
        transform 0.3s ease-in-out, -webkit-box-shadow 0.3s ease-in-out,
        -webkit-transform 0.3s ease-in-out;
      background-color: ${({ theme }) => theme.colors.slider.handle.background};
      box-sizing: border-box;
      box-shadow: ${({ theme }) => theme.colors.slider.handle.innerShadow},
        0 2px 3px 0 ${({ theme }) => theme.colors.slider.handle.shadow};
    }

    &.active::before {
      cursor: grabbing;
      transform: scale(1.15, 1.15);
    }

    &:hover .rs-tooltip,
    &.active .rs-tooltip {
      display: block;
      opacity: 1;
      top: unset;
      transform: translateX(0.25rem);
      bottom: -62px;
    }
  }

  .rs-tooltip {
    position: absolute;
    z-index: 1070; // Seems arbitrary, this is from the rsuite styles
    display: none;
    font-family: ${({ theme }) => theme.typography.font.brand};
    font-size: 0.75rem;
    opacity: 0;
    line-height: 1.66666667;
    margin-top: -6px;
    transform: translate(0, 0);
    bottom: -55px;
    top: unset;
    box-sizing: border-box;

    .rs-tooltip-arrow {
      border-color: transparent;
      position: absolute;
      width: 0;
      height: 0;
      border-color: transparent;
      border-style: solid;
      top: -5px;
      left: 50%;
      margin-left: -5px;
      border-width: 0 5px 5px;
      margin-top: 1px;

      &:after,
      &:before {
        border: solid transparent;
        content: ' ';
        height: 0;
        width: 0;
        position: absolute;
        pointer-events: none;
        bottom: 0%;
        left: 50%;
        margin-bottom: -0.3rem;
      }

      &:after {
        border-color: rgba(136, 183, 213, 0);
        border-bottom-color: ${({ theme }) => theme.colors.tooltip.background};
        border-width: 0.5rem;
        margin-left: -0.5rem;
      }

      &:before {
        border-color: rgba(194, 225, 245, 0);
        border-bottom-color: ${({ theme }) =>
          theme.colors.tooltip.success.border};
        border-width: 0.625rem;
        margin-left: -0.625rem;
      }
    }

    .rs-tooltip-inner {
      max-width: 250px;
      padding: 2px 10px;
      color: ${({ theme }) => theme.colors.slider.tooltip.foreground};
      text-align: center;
      background-color: ${({ theme }) =>
        theme.colors.slider.tooltip.background};
      border: 1px solid ${({ theme }) => theme.colors.slider.tooltip.border};
      overflow-wrap: break-word;
      box-sizing: border-box;
    }
  }

  .rs-slider-graduator {
    box-sizing: border-box;
    width: 100%;

    ul {
      display: flex;
      padding-left: 0;
      width: 100%;
      list-style: none;
      margin-top: 0;
      margin-bottom: 10px;
      box-sizing: border-box;

      li {
        flex: 1 1 1%;
        position: relative;
        list-style: none;
        box-sizing: border-box;

        &::before,
        &:last-child::after {
          content: '';
          display: block;
          position: absolute;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: ${({ theme }) =>
            theme.colors.slider.mark.normal.background};
          -webkit-box-sizing: border-box;
          box-sizing: border-box;
          border: 2px solid
            ${({ theme }) => theme.colors.slider.mark.normal.border};
          margin-left: -5px;
          top: -2px;
          -webkit-transition: border 0.2s ease-in;
          transition: border 0.2s ease-in;
        }

        &:last-child::after {
          right: -4px;
        }

        &.rs-slider-pass::before {
          border-color: ${({ theme }) =>
            theme.colors.slider.mark.active.border};
        }

        &.rs-slider-active::before {
          visibility: hidden;
        }

        .rs-slider-mark {
          color: ${({ theme }) => theme.colors.slider.mark.foreground};
          font-family: ${({ theme }) => theme.typography.font.brand};
          font-size: 0.625rem;
          position: absolute;
          top: 10px;
          left: -0.5px;
          white-space: nowrap;
          box-sizing: border-box;

          .rs-slider-mark-content {
            margin-left: -50%;
            box-sizing: border-box;
          }
        }

        .rs-slider-last-mark {
          left: auto;
          right: -10px;

          .rs-slider-mark-content {
            margin-left: 0;
          }
        }
      }
    }
  }
`

// We need to make the function signature backwards compatible, but leverage the styled-components babel plugin for template literals
const slider = (theme: KaizenTheme) =>
  styledSlider
    .map((interpolation) => {
      if (typeof interpolation === 'function') {
        return interpolation({ theme })
      }
      return interpolation
    })
    .join('')

export default {
  slider,
}
